import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import styles from './styles.module.css';
import ColorPalette from '../../../../colorpalette/ColorPalette';
import { Typography } from '@mui/material';
import CustomButton from '../../../../components/buttons/CustomButton';
import ModuleCard from '../moduleCard/ModuleCard';
import ModuleEmptyState from '../moduleCard/ModuleEmptyState';
import dayjs from 'dayjs';
import IconAdd from '@mui/icons-material/Add';
import IconEdit from '@mui/icons-material/Edit';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import CreateCranialAsymmetryEntryPopup from '../../../../popup/CreateCranialAsymmetryEntryPopup/CreateCranialAsymmetryEntryPopup';
import CranialAsymmetryChart from './CranialAsymmetryChart';
import ShareReportDialog from '../../../../components/common/ShareReportDialog/ShareReportDialog';
import ClinicalReportTemplate from '../../../../components/documents-templates/ClinicalReportTemplate';
import { buildCranialAsymmetryReportData, formatCranialAsymmetryHistory, buildTreatmentSummary, buildMeasurementChanges, buildCranialPhotoReport, listCranialReportPhotoRequests } from './cranialAsymmetryReportAdapter';
import { loadReportPhotoUrls } from '../../../../services/clinicalReportService';
import CranialSeverityChip from './CranialSeverityChip';
import { getApBpSeverity, getPlagiocephalySeverity } from './cranialSeverity';
import CranialPhotos from './photos/CranialPhotos';
import CranialPhotoCell from './photos/CranialPhotoCell';
import CranialPhotoViewSelect from './photos/CranialPhotoViewSelect';
import { CRANIAL_PHOTO_CONTEXT, buildCranialPhotoPayload, filterCranialPhotos, groupPhotosByEntry } from './photos/cranialPhotoViews';
import PatientAttachmentUploadPopup from '../../../../popup/PatientAttachmentUploadPopup/PatientAttachmentUploadPopup';
import { DataProviderInstance } from '../../../../api/DataProvider';

const CranialAsymmetry = ({ appointmentId, patientId, data, attachments, onUpdated, enabled, collapsible = false, patientName, companyData }) => {
    const { ListPatientAttachments, GetPatientAttachmentFile } = DataProviderInstance();

    const [history, setHistory] = React.useState(data ? data : []);
    const [showPopup, setShowPopup] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [showShareDialog, setShowShareDialog] = useState(false);
    const [photos, setPhotos] = useState(filterCranialPhotos(attachments));
    const [uploadEntry, setUploadEntry] = useState(null);
    const [includePhotos, setIncludePhotos] = useState(true);
    const [photoUrls, setPhotoUrls] = useState(null);
    const [loadingPhotoUrls, setLoadingPhotoUrls] = useState(false);
    const pdfContentRef = useRef(null);

    useEffect(() => {
        setHistory(data);
    }, [data]);

    useEffect(() => {
        setPhotos(filterCranialPhotos(attachments));
    }, [attachments]);

    /**
     * Recarrega só as fotos. Nunca chama onUpdated: uma foto adicionada,
     * editada ou excluída não pode recarregar a página, porque o profissional
     * pode estar com a evolução do atendimento em edição.
     */
    const loadPhotos = useCallback(() => {
        if (!patientId) return;

        ListPatientAttachments(patientId, CRANIAL_PHOTO_CONTEXT, (response) => {
            if (response && response.status === 'SUCCESS') {
                setPhotos(filterCranialPhotos(response.data || []));
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patientId]);

    const photosByEntry = groupPhotosByEntry(photos);

    /** Estrutura do registro fotográfico do PDF, ainda sem as object URLs. */
    const photoReportBase = useMemo(() => buildCranialPhotoReport(history, photos), [history, photos]);

    /**
     * As fotos só são baixadas quando o profissional abre o compartilhamento:
     * o template oculto fica montado o tempo todo e não pode puxar arquivo à
     * toa. O html2canvas não espera download, então o PDF só é liberado depois
     * que todos os blobs estão em memória.
     */
    useEffect(() => {
        if (!showShareDialog || !photoReportBase) return undefined;

        let active = true;

        // Descarta o resultado anterior: uma foto excluída tem a object URL
        // revogada pelo cache do AuthFile.
        setPhotoUrls(null);
        setLoadingPhotoUrls(true);

        loadReportPhotoUrls(GetPatientAttachmentFile, listCranialReportPhotoRequests(photoReportBase))
            .then((urls) => {
                if (!active) return;

                setPhotoUrls(urls);
                setLoadingPhotoUrls(false);
            })
            .catch(() => {
                if (active) setLoadingPhotoUrls(false);
            });

        return () => {
            active = false;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showShareDialog, photoReportBase]);

    const photoReport = useMemo(
        () => (includePhotos && photoUrls ? buildCranialPhotoReport(history, photos, photoUrls) : null),
        [includePhotos, photoUrls, history, photos]
    );

    const getChip = (value, label, color, textColor, isMobileCard = false) => (
        <CranialSeverityChip
            value={value}
            label={label}
            color={color}
            textColor={textColor}
            compact={isMobileCard}
        />
    );

    const formatDate = (date) => dayjs(date).format('DD/MM/YYYY');

    function popupHideHandler(needsReload) {
        setShowPopup(false);

        if (needsReload) {
            onUpdated();
        }
    }

    function addItemHandler() {
        setSelectedEntry(null);
        setShowPopup(true);
    }

    function addPhotosHandler(entry) {
        setUploadEntry(entry);
    }

    function uploadHideHandler(success) {
        setUploadEntry(null);

        if (success) loadPhotos();
    }
    
    const actions = (
        <>
            {history && history.length > 0 && (
                <CustomButton
                    variant="outlined"
                    style="primary"
                    label={"Compartilhar"}
                    icon={<ShareOutlinedIcon />}
                    onClick={() => setShowShareDialog(true)}
                />
            )}

            {enabled && (
                <CustomButton
                    variant="contained"
                    style="primary"
                    label={"Adicionar"}
                    icon={<IconAdd />}
                    onClick={addItemHandler}
                />
            )}
        </>
    );

    return (
        <>
            <ModuleCard
                accent="teal"
                title="Assimetria craniana"
                icon={<MonitorHeartOutlinedIcon />}
                description="Histórico de medições de assimetria craniana"
                actions={actions}
                collapsible={collapsible}
            >
                <div className={styles.contentContainer}>
                    {history.length == 0 ? (
                        <ModuleEmptyState icon={<MonitorHeartOutlinedIcon />} text="Não existem medições registradas." />
                    ) : (
                        <>
                            {/* Chart visualization - keep chronological order (oldest to newest) */}
                            <CranialAsymmetryChart data={[...history].sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf())} />
                            
                            {/* Table and Mobile Card visualization */}
                            <div className={styles.tableContainer}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: ColorPalette.textDark,
                                        marginBottom: '12px',
                                        fontWeight: 600
                                    }}
                                >
                                    Histórico de Medições ({history.length})
                                </Typography>
                                
                                {/* Desktop Table View */}
                                <div className={styles.customTableContainer}>
                                    <table className={styles.customTable}>
                                        <thead>
                                            <tr>
                                                <th>Data</th>
                                                <th style={{ textAlign: 'center' }}>AP</th>
                                                <th style={{ textAlign: 'center' }}>BP</th>
                                                <th style={{ textAlign: 'center' }}>Escala AP/BP</th>
                                                <th style={{ textAlign: 'center' }}>DD</th>
                                                <th style={{ textAlign: 'center' }}>DE</th>
                                                <th style={{ textAlign: 'center' }}>Plagiocefalia</th>
                                                <th style={{ textAlign: 'center' }}>Fotos</th>
                                                <th style={{ textAlign: 'center' }}>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...history]
                                              .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
                                              .map((item, index) => {
                                                const [plagioPercentage, plagioSeverity] = getPlagiocephalySeverity(item);
                                                const [otherPercentage, otherSeverity] = getApBpSeverity(item);
                                                return (
                                                    <tr key={item.id} className={index % 2 == 0 ? styles.evenRow : styles.oddRow}>
                                                        <td>{formatDate(item.createdAt)}</td>
                                                        <td style={{ textAlign: 'center' }}>{item.ap}</td>
                                                        <td style={{ textAlign: 'center' }}>{item.bp}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            {getChip(otherPercentage + "%", otherSeverity.severity, otherSeverity.color, otherSeverity.textColor)}
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>{item.dd}</td>
                                                        <td style={{ textAlign: 'center' }}>{item.de}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            {getChip(plagioPercentage + "%", plagioSeverity.severity, plagioSeverity.color, plagioSeverity.textColor)}
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <CranialPhotoCell
                                                                photos={photosByEntry[String(item.id)]}
                                                                enabled={enabled}
                                                                onAdd={() => addPhotosHandler(item)}
                                                                onChanged={loadPhotos}
                                                            />
                                                        </td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            {enabled && (
                                                                <CustomButton
                                                                    style="primary"
                                                                    icon={<IconEdit fontSize="small" />}
                                                                    onClick={() => {
                                                                        setSelectedEntry(item);
                                                                        setShowPopup(true);
                                                                    }}
                                                                    enabled={enabled}
                                                                    rounded={true}
                                                                    outlined={true}
                                                                />
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                                
                                {/* Mobile Card View */}
                                <div className={styles.mobileCardContainer}>
                                    {[...history]
                                      .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
                                      .map((item, index) => {
                                        const [plagioPercentage, plagioSeverity] = getPlagiocephalySeverity(item);
                                        const [otherPercentage, otherSeverity] = getApBpSeverity(item);
                                        
                                        return (
                                            <div key={item.id} className={styles.mobileCard}>
                                                <div className={styles.mobileCardHeader}>
                                                    <div className={styles.mobileCardDate}>
                                                        {formatDate(item.createdAt)}
                                                    </div>
                                                    {enabled && (
                                                        <CustomButton
                                                            style="primary"
                                                            icon={<IconEdit fontSize="small" />}
                                                            onClick={() => {
                                                                setSelectedEntry(item);
                                                                setShowPopup(true);
                                                            }}
                                                            enabled={enabled}
                                                            rounded={true}
                                                            outlined={true}
                                                        />
                                                    )}
                                                </div>
                                                
                                                <div className={styles.mobileCardRow}>
                                                    <div className={styles.mobileCardLabel}>AP:</div>
                                                    <div className={styles.mobileCardValue}>{item.ap}</div>
                                                </div>
                                                
                                                <div className={styles.mobileCardRow}>
                                                    <div className={styles.mobileCardLabel}>BP:</div>
                                                    <div className={styles.mobileCardValue}>{item.bp}</div>
                                                </div>
                                                
                                                <div className={styles.mobileCardRow}>
                                                    <div className={styles.mobileCardLabel}>Escala AP/BP:</div>
                                                    <div className={styles.mobileCardValue}>
                                                        {getChip(otherPercentage + "%", otherSeverity.severity, otherSeverity.color, otherSeverity.textColor, true)}
                                                    </div>
                                                </div>
                                                
                                                <div className={styles.mobileCardRow}>
                                                    <div className={styles.mobileCardLabel}>DD:</div>
                                                    <div className={styles.mobileCardValue}>{item.dd}</div>
                                                </div>
                                                
                                                <div className={styles.mobileCardRow}>
                                                    <div className={styles.mobileCardLabel}>DE:</div>
                                                    <div className={styles.mobileCardValue}>{item.de}</div>
                                                </div>
                                                
                                                <div className={styles.mobileCardRow}>
                                                    <div className={styles.mobileCardLabel}>Plagiocefalia:</div>
                                                    <div className={styles.mobileCardValue}>
                                                        {getChip(plagioPercentage + "%", plagioSeverity.severity, plagioSeverity.color, plagioSeverity.textColor, true)}
                                                    </div>
                                                </div>

                                                <div className={styles.mobileCardRow}>
                                                    <div className={styles.mobileCardLabel}>Fotos:</div>
                                                    <div className={styles.mobileCardValue}>
                                                        <CranialPhotoCell
                                                            photos={photosByEntry[String(item.id)]}
                                                            enabled={enabled}
                                                            onAdd={() => addPhotosHandler(item)}
                                                            onChanged={loadPhotos}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Antes e depois: primeira × última medição com foto, por vista */}
                            <CranialPhotos
                                entries={history}
                                photos={photos}
                                enabled={enabled}
                                onChanged={loadPhotos}
                            />
                        </>
                    )}
                </div>
            </ModuleCard>

            {showPopup && <CreateCranialAsymmetryEntryPopup appointmentId={appointmentId} patientId={patientId} OnHide={popupHideHandler} entry={selectedEntry} photos={selectedEntry ? (photosByEntry[String(selectedEntry.id)] || []) : []} onPhotosChanged={loadPhotos} />}

            {/* Fluxo B: fotos a partir da tabela, já com a medição e a data certas */}
            {uploadEntry && (
                <PatientAttachmentUploadPopup
                    patientId={patientId}
                    appointmentId={appointmentId}
                    context={CRANIAL_PHOTO_CONTEXT}
                    contextId={uploadEntry.id}
                    defaultDate={dayjs(uploadEntry.createdAt).format('YYYY-MM-DD')}
                    capture={true}
                    maxFiles={10}
                    accept="image/*"
                    title={`Fotos da medição de ${formatDate(uploadEntry.createdAt)}`}
                    renderItemFields={(file, value, onChange) => (
                        <CranialPhotoViewSelect
                            value={(value || {}).view}
                            onChange={(view) => onChange({ ...(value || {}), view: view })}
                        />
                    )}
                    buildItemPayload={buildCranialPhotoPayload}
                    OnHide={uploadHideHandler}
                />
            )}

            {/* Hidden PDF template for report generation */}
            {history && history.length > 0 && (
                <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
                    <ClinicalReportTemplate
                        ref={pdfContentRef}
                        companyData={companyData}
                        patientName={patientName}
                        reportTitle="Avaliação de assimetria craniana"
                        assessmentDate={buildCranialAsymmetryReportData(history, patientName)?.assessmentDate || ''}
                        treatmentSummary={buildTreatmentSummary(history)}
                        measurements={buildCranialAsymmetryReportData(history, patientName)?.measurements || []}
                        classification={buildCranialAsymmetryReportData(history, patientName)?.classification || ''}
                        measurementChanges={buildMeasurementChanges(history)}
                        photoReport={photoReport}
                        historyTable={{
                            columns: [
                                { label: 'Data', key: 'date' },
                                { label: 'AP', key: 'ap' },
                                { label: 'BP', key: 'bp' },
                                { label: 'AP/BP %', key: 'apBpPercentage' },
                                { label: 'AP/BP', key: 'apBpSeverity' },
                                { label: 'DD', key: 'dd' },
                                { label: 'DE', key: 'de' },
                                { label: 'Plagio %', key: 'plagioPercentage' },
                                { label: 'Plagio', key: 'plagioSeverity' },
                            ],
                            rows: formatCranialAsymmetryHistory(history),
                        }}
                    >
                        {/* Evolution charts for PDF */}
                        <div style={{ marginTop: '8px', marginBottom: '16px' }}>
                            <CranialAsymmetryChart
                                data={[...history].sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf())}
                            />
                        </div>
                    </ClinicalReportTemplate>
                </div>
            )}

            {/* Share dialog */}
            {showShareDialog && history && history.length > 0 && (
                <ShareReportDialog
                    reportData={buildCranialAsymmetryReportData(history, patientName)}
                    pdfContentRef={pdfContentRef}
                    reportType="assimetria-craniana"
                    hasPhotos={!!photoReportBase}
                    includePhotos={includePhotos}
                    onIncludePhotosChange={setIncludePhotos}
                    preparingPhotos={loadingPhotoUrls}
                    onClose={() => setShowShareDialog(false)}
                />
            )}
        </>
    );
};

export default CranialAsymmetry;
